import { Check, Loader2, Printer, RotateCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { PrintTicketStatus } from '@/api/qrCode.api';

interface PrintProgressProps {
  tickets: PrintTicketStatus[];
  isPrinting: boolean;
  /** The batch never came back at all — printer unreachable, request refused. */
  batchError?: string | null;
  onRetry?: () => void;
  onReprintOne?: (qrId: string) => void;
}

export function PrintProgress({
  tickets,
  isPrinting,
  batchError = null,
  onRetry,
  onReprintOne,
}: PrintProgressProps) {
  const failed = tickets.filter((t) => t.status === 'FAILED');

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-ink-600">
        <Printer className="h-4 w-4 text-primary" />
        Mencetak tiket
        {isPrinting && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
      </div>

      <ul className="max-h-48 space-y-1.5 overflow-y-auto pos-scroll">
        {tickets.map((t) => {
          const ok = t.status === 'PRINTED';
          const bad = t.status === 'FAILED';
          return (
            <li
              key={t.id}
              className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-sm"
            >
              <span className="tnum truncate text-ink-600">{t.id}</span>
              <span className="flex items-center gap-2">
                <span
                  className={cn(
                    'inline-flex h-6 w-6 items-center justify-center rounded-full',
                    ok && 'bg-success/15 text-success',
                    bad && 'bg-destructive/15 text-destructive',
                    !ok && !bad && 'bg-secondary text-ink-400',
                  )}
                >
                  {ok ? <Check className="h-4 w-4" /> : bad ? <X className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />}
                </span>
                {bad && onReprintOne && (
                  <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => onReprintOne(t.id)}>
                    <RotateCw className="h-3.5 w-3.5" /> Ulang
                  </Button>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {batchError && onRetry && (
        <div className="rounded-md border-2 border-destructive/30 bg-destructive/5 p-3">
          {/* The payment already succeeded — say so first, because the only
              thing on screen otherwise is a printer that did not answer. */}
          <p className="text-sm font-medium text-destructive">
            Tiket gagal dicetak — printer tidak menjawab. Transaksi sudah berhasil dan tiket sudah
            terbit; cetak ulang dari menu Pesanan kapan saja.
          </p>
          <p className="mt-1 text-xs text-ink-400">{batchError}</p>
          <Button variant="outline" size="sm" className="mt-2 gap-1.5" onClick={onRetry}>
            <RotateCw className="h-4 w-4" /> Coba cetak lagi
          </Button>
        </div>
      )}

      {failed.length > 0 && onRetry && (
        <div className="rounded-md border-2 border-destructive/30 bg-destructive/5 p-3">
          <p className="text-sm font-medium text-destructive">
            {failed.length} tiket gagal dicetak. Transaksi sudah berhasil — cetak ulang kapan saja
            dari menu Pesanan.
          </p>
          <Button variant="outline" size="sm" className="mt-2 gap-1.5" onClick={onRetry}>
            <RotateCw className="h-4 w-4" /> Cetak ulang semua yang gagal
          </Button>
        </div>
      )}
    </div>
  );
}
